/* tslint:disable */
/* eslint-disable */

import * as runtime from '../runtime';
import { BaseAPI } from '../baseApi';
import { Activity, ActivityEvaluation, ActivityEvaluationInput, ActivityInstance } from '../types';
import { HttpStatusCode } from 'axios';
import { ResponseError } from '../runtime';
import { compressImageToSize } from './CompressImage';
import { base64ToFile, getExtension, ImageFileBase64 } from '@/utils/image';

export interface CreateActivityRequest {
  activity: Activity;
  supervisedTag?: string;
}

export interface CompleteActivityRequest {
  activityInstanceId: number;
  supervisedTag?: string;
}

export interface EditActivityRequest {
  activity: Activity;
  supervisedTag?: string;
}

export interface CreateActivityOffspringRequest {
  activityInstance: ActivityInstance;
  activity: Activity;
  supervisedTag?: string;
}

export interface GetActivityInstancesRequest {
  startDate: Date;
  endDate: Date;
  supervisedTag?: string;
}

export interface GetActivityRequest {
  activityId: number;
  supervisedTag?: string;
}

export interface GetActivitiesByParamRequest {
  param: string;
  supervisedTag?: string;
}

export interface GetAllActivitiesRequest {
  supervisedTag?: string;
}

export interface GetActivityInstanceRequest {
  activityInstanceId: number;
  supervisedTag?: string;
}

export interface RemoveActivityRequest {
  activityId: number;
  supervisedTag?: string;
}

export interface RemoveActivityInstanceRequest {
  activityInstanceId: number;
  supervisedTag?: string;
}

export interface StartActivityRequest {
  activityInstanceId: number;
  supervisedTag?: string;
}

export interface SetFeelingActivityRequest {
  activityInstanceId: number;
  feelingOfActivity: number;
  supervisedTag?: string;
}

export interface SetSubtaskItemRequest {
  activityInstanceId: number;
  subtaskId: number;
  supervisedTag?: string;
}

export interface UploadImageRequest {
  image: File;
  url: string;
  supervisedTag?: string;
}

export interface ImagesFromActivityRequest {
  activityId: number;
  supervisedTag?: string;
}

/**
 * Kö av föreslagna stjärnvärden. Läsning i båda riktningarna: MED supervisedTag läser
 * föräldern barnets kö, UTAN läser brukaren sin egen (tonåringen ser vad som väntar på
 * besked). BESLUTEN nedan kräver däremot alltid supervisedTag - de är förälderns.
 */
export interface StarValueProposalsRequest {
  supervisedTag?: string;
}

/**
 * Godkänner ett förslag. `starValue` utelämnad = godkänn det föreslagna värdet som det är;
 * satt = föräldern fastställer ett annat värde (1-5) utan att behöva neka förslaget.
 */
export interface ApproveStarValueRequest {
  activityId: number;
  starValue?: number;
  supervisedTag: string;
}

export interface RejectStarValueRequest {
  activityId: number;
  supervisedTag: string;
}

type GetImagesResponse = {
  activityImageFile?: ImageFileBase64;
  subtaskImageFiles: { [subtaskId: number]: ImageFileBase64 };
};

export class ActivityApi extends BaseAPI {
  async createActivity(
    requestParameters: CreateActivityRequest,
  ): Promise<HttpStatusCode | ResponseError | undefined> {
    const queryParameters: any = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activities`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: runtime.toRequestBody(requestParameters.activity),
      });

      if (response.data) {
        await this.handleImageUpload(response.data, requestParameters.activity);
      }

      return response.status;
    } catch (error: unknown) {
      if (error instanceof ResponseError) return error;
    }
  }

  async completeActivity(
    requestParameters: CompleteActivityRequest,
  ): Promise<HttpStatusCode | undefined> {
    const queryParameters: any = {};

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activity-instances/${requestParameters.activityInstanceId}/complete`,
        method: 'PUT',
        query: queryParameters,
      });

      return response.status;
    } catch (error) {}
  }

  async editActivity(
    requestParameters: EditActivityRequest,
  ): Promise<HttpStatusCode | ResponseError | undefined> {
    const queryParameters: any = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activities`,
        method: 'PUT',
        headers: headerParameters,
        query: queryParameters,
        body: runtime.toRequestBody(requestParameters.activity),
      });

      if (response.data) {
        await this.handleImageUpload(response.data, requestParameters.activity);
      }

      return response.status;
    } catch (error) {
      if (error instanceof ResponseError) return error;
    }
  }

  async createActivityOffspring(
    requestParameters: CreateActivityOffspringRequest,
  ): Promise<HttpStatusCode | ResponseError | undefined> {
    const queryParameters: any = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }
    if (requestParameters.activityInstance.activityInstanceId !== undefined) {
      queryParameters['paramActivityInstanceId'] =
        requestParameters.activityInstance.activityInstanceId;
    }

    try {
      const response = await this.request({
        path: `/activity-instances`,
        method: 'PUT',
        headers: headerParameters,
        query: queryParameters,
        body: runtime.toRequestBody(requestParameters.activity),
      });

      if (response.data) {
        await this.handleImageUpload(response.data, requestParameters.activity);
      }

      return response.status;
    } catch (error) {
      if (error instanceof ResponseError) return error;
    }
  }

  async getActivityInstances(
    requestParameters: GetActivityInstancesRequest,
  ): Promise<Array<ActivityInstance> | undefined> {
    const queryParameters: any = {};

    if (requestParameters.startDate !== undefined) {
      queryParameters['intervalStartDate'] = (requestParameters.startDate as any)
        .toISOString()
        .slice(0, 10);
    }

    if (requestParameters.endDate !== undefined) {
      queryParameters['intervalEndDate'] = (requestParameters.endDate as any)
        .toISOString()
        .slice(0, 10);
    }

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activity-instances`,
        method: 'GET',
        query: queryParameters,
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): Array<ActivityInstance> => jsonValue,
      ).value();
    } catch (error) {}
  }

  async getActivity(requestParameters: GetActivityRequest): Promise<Activity | undefined> {
    const queryParameters: any = {};

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activities/${requestParameters.activityId}`,
        method: 'GET',
        query: queryParameters,
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): Activity => jsonValue,
      ).value();
    } catch (error) {}
  }

  /** Aktiviteter där barnet/tonåringen föreslagit ett stjärnvärde som väntar på beslut. */
  async getStarValueProposals(
    requestParameters: StarValueProposalsRequest,
  ): Promise<Activity[] | undefined> {
    const queryParameters: any = {};

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activities/star-value-proposals`,
        method: 'GET',
        query: queryParameters,
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): Activity[] => jsonValue,
      ).value();
    } catch (error) {}
  }

  async approveStarValue(
    requestParameters: ApproveStarValueRequest,
  ): Promise<HttpStatusCode | undefined> {
    const queryParameters: any = { supervisedTag: requestParameters.supervisedTag };
    const headerParameters: runtime.HTTPHeaders = { 'Content-Type': 'application/json' };

    try {
      const response = await this.request({
        path: `/activities/${requestParameters.activityId}/star-value/approve`,
        method: 'PUT',
        headers: headerParameters,
        query: queryParameters,
        body: runtime.toRequestBody({ starValue: requestParameters.starValue ?? null }),
      });

      return response.status;
    } catch (error) {}
  }

  async rejectStarValue(
    requestParameters: RejectStarValueRequest,
  ): Promise<HttpStatusCode | undefined> {
    const queryParameters: any = { supervisedTag: requestParameters.supervisedTag };

    try {
      const response = await this.request({
        path: `/activities/${requestParameters.activityId}/star-value/reject`,
        method: 'PUT',
        query: queryParameters,
      });

      return response.status;
    } catch (error) {}
  }

  /**
   * ALLA aktiviteter för användaren (eller brukaren, med supervisedTag).
   *
   * getActivitiesByParam är INTE samma sak och duger inte som ersättning: den är ett
   * sökförslag och svarar avdubblerat på namn+beskrivning, med högst fyra träffar. Den
   * som ska visa vad kontot faktiskt har — handledarens rensning, rutinuppslagningen —
   * måste fråga här, annars saknas både dubbletterna och allt efter den fjärde.
   */
  async getAllActivities(
    requestParameters: GetAllActivitiesRequest,
  ): Promise<Activity[] | undefined> {
    const queryParameters: any = {};

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activities/all`,
        method: 'GET',
        query: queryParameters,
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): Activity[] => jsonValue,
      ).value();
    } catch (error) {}
  }

  async getActivitiesByParam(
    requestParameters: GetActivitiesByParamRequest,
  ): Promise<Activity[] | undefined> {
    const queryParameters: any = { activityName: requestParameters.param };

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activities`,
        method: 'GET',
        query: queryParameters,
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): Activity[] => jsonValue,
      ).value();
    } catch (error) {}
  }

  async getActivityInstance(
    requestParameters: GetActivityInstanceRequest,
  ): Promise<ActivityInstance | undefined> {
    const queryParameters: any = {};

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activity-instances/${requestParameters.activityInstanceId}`,
        method: 'GET',
        query: queryParameters,
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): ActivityInstance => jsonValue,
      ).value();
    } catch (error) {}
  }

  async removeActivity(
    requestParameters: RemoveActivityRequest,
  ): Promise<HttpStatusCode | undefined> {
    const queryParameters: any = {};

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activities/${requestParameters.activityId}`,
        method: 'DELETE',
        query: queryParameters,
      });

      return response.status;
    } catch (error) {}
  }

  async removeActivityInstance(
    requestParameters: RemoveActivityInstanceRequest,
  ): Promise<HttpStatusCode | undefined> {
    const queryParameters: any = {};

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activity-instances/${requestParameters.activityInstanceId}`,
        method: 'DELETE',
        query: queryParameters,
      });

      return response.status;
    } catch (error) {}
  }

  async startActivity(
    requestParameters: StartActivityRequest,
  ): Promise<HttpStatusCode | undefined> {
    const queryParameters: any = {};

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activity-instances/${requestParameters.activityInstanceId}/start`,
        method: 'PUT',
        query: queryParameters,
      });

      return response.status;
    } catch (error) {}
  }

  async setActivityFeeling(
    requestParameters: SetFeelingActivityRequest,
  ): Promise<HttpStatusCode | undefined> {
    const queryParameters: any = {};

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activity-instances/${requestParameters.activityInstanceId}/set-feeling`,
        method: 'PUT',
        query: queryParameters,
        body: runtime.toRequestBody({ feelingOfActivity: requestParameters.feelingOfActivity }),
      });

      return response.status;
    } catch (error) {}
  }

  /**
   * Utvärderingen efter en avklarad aktivitet (V012): känsla, energi, svarsläge och taggar
   * i ett anrop. Ersätter setActivityFeeling. Kastar vid fel, så att vyn kan säga att
   * svaret inte sparades i stället för att visa det som sparat.
   */
  async setActivityEvaluation(
    activityInstanceId: number,
    evaluation: ActivityEvaluationInput,
    supervisedTag?: string,
  ): Promise<void> {
    const queryParameters: any = {};
    if (supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = supervisedTag;
    }
    await this.request({
      path: `/activity-instances/${activityInstanceId}/evaluation`,
      method: 'PUT',
      query: queryParameters,
      headers: { 'Content-Type': 'application/json' },
      body: runtime.toRequestBody(evaluation),
    });
  }

  /** Den sparade utvärderingen, eller null när ingen har svarat (204) eller anropet misslyckas. */
  async getActivityEvaluation(
    activityInstanceId: number,
    supervisedTag?: string,
  ): Promise<ActivityEvaluation | null> {
    const queryParameters: any = {};
    if (supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = supervisedTag;
    }
    try {
      const response = await this.request({
        path: `/activity-instances/${activityInstanceId}/evaluation`,
        method: 'GET',
        query: queryParameters,
      });
      if (response.status === 204) return null;
      return (response.data as ActivityEvaluation) ?? null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async setSubTask(requestParameters: SetSubtaskItemRequest): Promise<HttpStatusCode | undefined> {
    const queryParameters: any = {};

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activity-instances/${requestParameters.activityInstanceId}/set-subtask`,
        method: 'PUT',
        query: queryParameters,
        body: runtime.toRequestBody({ subtaskId: requestParameters.subtaskId }),
      });

      return response.status;
    } catch (error) {}
  }

  async handleImageUpload(data: JSON, activity: Activity): Promise<void> {
    Object.entries(data).map(([key, value]) => {
      const image = runtime.getImageFile(activity, key);
      if (image) {
        this.uploadImageRequest({
          image: image,
          url: String(value),
        });
      }
    });
  }

  async uploadImageRequest(
    requestParameters: UploadImageRequest,
  ): Promise<HttpStatusCode | undefined> {
    const queryParameters: any = {};

    const headerParameters: runtime.HTTPHeaders = {};

    // Initialize quality and blob
    const maxFileSize = 3 * 1024 * 1024;

    const blob = await compressImageToSize(requestParameters.image, maxFileSize);

    if (blob === null) return undefined;

    // headerParameters['content-type'] = compressedFile.type;
    headerParameters['content-type'] = blob.type;

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `${requestParameters.url}`,
        method: 'PUT',
        headers: headerParameters,
        query: queryParameters,
        body: blob,
      });

      return response.status;
    } catch (error) {
      return undefined;
    }
    return undefined;
  }

  async getImagesFromActivity(requestParameters: ImagesFromActivityRequest): Promise<
    | {
        activityImage?: File;
        subtaskImages?: { [subtaskId: number]: File };
      }
    | undefined
  > {
    const queryParameters: any = {};
    const headerParameters: runtime.HTTPHeaders = {};

    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/activities/picture/${requestParameters.activityId}`,
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      });

      const data = response.data as GetImagesResponse;

      const activityImage = data.activityImageFile
        ? base64ToFile(
            data.activityImageFile.base64,
            `activity-${requestParameters.activityId}${getExtension(
              data.activityImageFile.mimeType,
            )}`,
            data.activityImageFile.mimeType,
          )
        : undefined;

      const subtaskImages: { [subtaskId: number]: File } = {};
      for (const [subtaskId, imageFile] of Object.entries(data.subtaskImageFiles)) {
        subtaskImages[+subtaskId] = base64ToFile(
          imageFile.base64,
          `subtask-${subtaskId}${getExtension(imageFile.mimeType)}`,
          imageFile.mimeType,
        );
      }

      return {
        activityImage,
        subtaskImages,
      };
    } catch (error) {
      console.error('Error fetching images:', error);
      return undefined;
    }
  }
}
